// src/components/DictionaryBuilder.jsx
import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const pkfkOptions = [
  { value: "", label: "None" },
  { value: "PK", label: "Primary Key" },
  { value: "FK", label: "Foreign Key" },
];

const typeOptions = ["Boolean", "Date", "Float", "Integer", "Timestamp", "VARCHAR"];

/* ------------------------ SMALL INPUT COMPONENTS ------------------------ */
const InlineInput = React.memo(function InlineInput({
  value,
  onChange,
  disabled = false,
  type = "text",
  style,
}) {
  return (
    <input
      type={type}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        border: "none",
        borderBottom: "1px solid #ccc",
        background: "transparent",
        padding: "2px 4px",
        font: "inherit",
        outline: "none",
        ...style,
      }}
    />
  );
});

const InlineSelect = React.memo(function InlineSelect({
  value,
  options,
  onChange,
  disabled = false,
}) {
  return (
    <select
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        border: "none",
        borderBottom: "1px solid #ccc",
        background: "transparent",
        padding: "2px 4px",
        font: "inherit",
        outline: "none",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value ?? opt} value={opt.value ?? opt}>
          {opt.label ?? opt}
        </option>
      ))}
    </select>
  );
});

/* ----------------------------- COLUMN ROW ------------------------------ */
const ColumnRow = React.memo(function ColumnRow({ col, onChange, onDelete }) {
  return (
    <TableRow>
      <TableCell>
        <InlineInput value={col.name} onChange={(v) => onChange("name", v)} />
      </TableCell>
      <TableCell>
        <InlineSelect
          value={col.type}
          options={typeOptions}
          onChange={(v) => onChange("type", v)}
        />
      </TableCell>
      <TableCell>
        <InlineSelect
          value={col.key}
          options={pkfkOptions}
          onChange={(v) => onChange("key", v)}
        />
      </TableCell>
      <TableCell>
        <InlineInput
          value={col.refTable}
          disabled={col.key !== "FK"}
          onChange={(v) => onChange("refTable", v)}
        />
      </TableCell>
      <TableCell>
        <InlineInput
          value={col.refColumn}
          disabled={col.key !== "FK"}
          onChange={(v) => onChange("refColumn", v)}
        />
      </TableCell>
      <TableCell>
        <InlineInput
          value={col.description}
          onChange={(v) => onChange("description", v)}
        />
      </TableCell>
      <TableCell align="center">
        <Tooltip title="Delete column">
          <IconButton onClick={onDelete} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
});

/* -------------------------- MAIN BUILDER -------------------------- */
export default function DictionaryBuilder({ tables, setTables, readOnly = false }) {
  const [localTables, setLocalTables] = useState(tables);

  // Debounce update to parent
  useEffect(() => {
    const t = setTimeout(() => setTables(localTables), 250);
    return () => clearTimeout(t);
  }, [localTables, setTables]);

  useEffect(() => {
    setLocalTables(tables);
  }, [tables]);

  const updateTable = useCallback((tableIndex, fn) => {
    setLocalTables((prev) => {
      const next = [...prev];
      next[tableIndex] = fn(next[tableIndex]);
      return next;
    });
  }, []);

  const handleAddTable = () => {
    setLocalTables([
      ...localTables,
      {
        name: "",
        description: "",
        synapse_path: "",
        columns: [
          { name: "", type: "", key: "", refTable: "", refColumn: "", description: "" },
        ],
      },
    ]);
  };

  const handleAddColumn = (tIdx) => {
    updateTable(tIdx, (tbl) => ({
      ...tbl,
      columns: [
        ...tbl.columns,
        { name: "", type: "", key: "", refTable: "", refColumn: "", description: "" },
      ],
    }));
  };

  const handleDeleteColumn = (tIdx, cIdx) => {
    updateTable(tIdx, (tbl) => ({
      ...tbl,
      columns: tbl.columns.filter((_, i) => i !== cIdx),
    }));
  };

  const updateColumn = (tIdx, cIdx, field, value) => {
    updateTable(tIdx, (tbl) => {
      const cols = [...tbl.columns];
      cols[cIdx] = { ...cols[cIdx], [field]: value };
      return { ...tbl, columns: cols };
    });
  };

  const updateTableMeta = (tIdx, field, value) => {
    updateTable(tIdx, (tbl) => ({ ...tbl, [field]: value }));
  };

  return (
    <Box>
      <Stack spacing={4}>
        {localTables.map((table, tIdx) => (
          <Box key={tIdx}>
            {/* Table Name + Description + Synapse Path */}
            <Stack direction="row" spacing={4} alignItems="flex-start" sx={{ mb: 2 }}>
              <Stack direction="column" spacing={0.5} sx={{ width: 180 }}>
                <Typography variant="caption" color="text.secondary">
                  Table Name
                </Typography>
                <InlineInput
                  value={table.name}
                  onChange={(v) => updateTableMeta(tIdx, "name", v)}
                  style={{ fontWeight: 600 }}
                  disabled={readOnly}
                />
              </Stack>

              <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Table Description
                </Typography>
                <InlineInput
                  value={table.description}
                  onChange={(v) => updateTableMeta(tIdx, "description", v)}
                  disabled={readOnly}
                />
              </Stack>

              <Stack direction="column" spacing={0.5} sx={{ width: 260 }}>
                <Typography variant="caption" color="text.secondary">
                  Synapse Path <span style={{ opacity: 0.6 }}>(optional, e.g. data/orders/**/*.parquet)</span>
                </Typography>
                <InlineInput
                  value={table.synapse_path || ""}
                  onChange={(v) => updateTableMeta(tIdx, "synapse_path", v)}
                  disabled={readOnly}
                />
              </Stack>
            </Stack>

            {/* Columns Table */}
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "15%" }}>Name</TableCell>
                    <TableCell sx={{ width: "10%" }}>Type</TableCell>
                    <TableCell sx={{ width: "8%" }}>Key</TableCell>
                    <TableCell sx={{ width: "8%" }}>Ref Table</TableCell>
                    <TableCell sx={{ width: "8%" }}>Ref Column</TableCell>
                    <TableCell sx={{ width: "46%" }}>Description</TableCell>
                    <TableCell sx={{ width: "5%" }} align="center">Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {table.columns.map((col, cIdx) => (
                    <ColumnRow
                      key={cIdx}
                      col={col}
                      onChange={(field, val) => updateColumn(tIdx, cIdx, field, val)}
                      onDelete={() => handleDeleteColumn(tIdx, cIdx)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!readOnly && (
              <Button size="small" onClick={() => handleAddColumn(tIdx)}>
                + Add Column
              </Button>
            )}

            {/* Divider between tables */}
            {tIdx < localTables.length - 1 && <Divider sx={{ my: 3 }} />}
          </Box>
        ))}

        {!readOnly && (
          <Button variant="outlined" onClick={handleAddTable}>
            + Add Table
          </Button>
        )}
      </Stack>
    </Box>
  );
}